import * as React from "react";
import { observer } from "mobx-react-lite";
import { PropsCell } from "src/components/Sheet2/PropsCell2";
import { Button, Dropdown, MenuProps, Space } from "antd";
import {
  ClearOutlined,
  EditOutlined,
  MenuOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { KeyDef } from "src/components/Sheet2/isSymbolKey";
import { EntityFiltersPageStore } from "src/pages/EntityFiltersPage/EntityFiltersPageStore";
import { ZEntityRow } from "src/pages/EntityFiltersPage/EntityList/types";
import { multiStepOnRowClick } from "src/components/MultiStepTask/utils";
import { ZUid } from "src/types/zUid";
import { onError } from "src/common/onError";
import { imgSrc } from "src/components/PlmUploader/imgSrc";
import { Bom3MaterialModal } from "../CellBom3MaterialSelect/Bom3MaterialModal";
import { Cell2WithModal } from "../Cell2WithModal";
import { Bom3PositionStore } from "../../Bom3PositionsStore";
import { ZBom3PosCellValue } from "../../ZBom3Position";
import styles from "./Cell2ColorModel.module.less";

type PropsCell2ColorModel = {
  cell: PropsCell;
  objColorModel: ZUid | null | undefined;
  positionStore: Bom3PositionStore;
  cellValue: ZBom3PosCellValue | undefined;
  disabled: boolean;
  title: string;
  onFocus: (e: FocusEvent) => void;
};

const colorModelNA = "";

// После долгой разработки выяснилось, что нужно выбирать не colorModel, а color. Но всё переименовывать уже не хочется.

export const Cell2ColorModel: React.FC<PropsCell2ColorModel> = observer(
  (props) => {
    const {
      cell,
      objColorModel,
      positionStore,
      cellValue,
      disabled,
      title,
      onFocus,
    } = props;
    const { store } = cell;
    const btnRef = React.useRef<HTMLElement>(null);
    const [menuActive, setMenuActive] = React.useState(false);
    const handleOpen = () => {
      setMenuActive(true);
    };

    const [editorStore, setEditorStore] =
      React.useState<EntityFiltersPageStore | null>(null);

    const handleEdit = () => {
      if (objColorModel) {
        const onRowClick = (row: ZEntityRow) => {
          multiStepOnRowClick(row, entityStore);
        };
        const entityStore = new EntityFiltersPageStore({
          objectId: objColorModel,
          selectionSettings: { selectionType: "radio" },
          onRowClick,
          actions: new Set(), // Чтобы не было кнопки создания модели, т.к. она вызывает переход на другую страницу.
          title,
        });
        setEditorStore(entityStore);
      }
    };

    const isKeySpecial = (e: KeyDef): boolean => {
      if (e.code === "Enter") {
        return true;
      }
      return false;
    };

    const activateCurrentCell = () => {
      store.activate(null, false);
      // После закрытия выпадающего меню желательно вернуть фокус на ячейку, чтобы можно было продолжать работать клавишами
      setTimeout(() => store.activate(cell.cellKey, false), 100);
    };

    const setValue = (value: string | null) => {
      positionStore
        .directChangeValue(cell.cellKey, value)
        .finally(() => activateCurrentCell())
        .catch(onError);
    };

    const items: MenuProps["items"] = [
      {
        key: "edit",
        label: "Редактировать",
        icon: <EditOutlined />,
        onClick: handleEdit,
      },
      {
        key: "na",
        label: "Не применять",
        icon: <StopOutlined />,
        onClick: () => setValue(colorModelNA),
      },
      {
        key: "clear",
        label: "Очистить",
        icon: <ClearOutlined />,
        onClick: () => setValue(null),
      },
    ];
    let valueView: React.ReactNode = null;
    if (cellValue?.value === colorModelNA) {
      valueView = (
        <Space>
          <StopOutlined />
          <span>N/A</span>
        </Space>
      );
    } else {
      valueView = (
        <>
          {cellValue?.colorModelName && <div>{cellValue.colorModelName}</div>}
          {cellValue?.color && (
            <div>
              <div
                className={styles.colorValue}
                style={{ backgroundColor: cellValue.color }}
                title={cellValue.color}
              />
            </div>
          )}
          {cellValue?.image && (
            <div>
              <img
                className={styles.imageValue}
                src={imgSrc(cellValue.image, "small")}
                alt=""
              />
            </div>
          )}
        </>
      );
    }

    return (
      <Cell2WithModal
        cell={cell}
        btnRef={btnRef}
        isKeySpecial={isKeySpecial}
        onFocus={onFocus}
      >
        <div className={styles.cell}>
          <div className={styles.info}>{valueView}</div>
          <Dropdown
            disabled={disabled}
            placement="bottomRight"
            open={menuActive}
            autoFocus
            onOpenChange={(isOpen) => {
              if (!isOpen) {
                setMenuActive(false);
                activateCurrentCell();
              }
            }}
            menu={{
              // TODO: Сейчас нет связи между текущим значением и тем элементом меню, который активен при открытии
              items,
            }}
          >
            <Button
              ref={btnRef}
              onClick={handleOpen}
              icon={<MenuOutlined />}
              disabled={disabled}
            />
          </Dropdown>
        </div>
        <Bom3MaterialModal
          store={editorStore}
          close={() => setEditorStore(null)}
          success={(row) => {
            setValue(String(row.id));
          }}
        />
      </Cell2WithModal>
    );
  },
);
